import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/errors";
import { useDeleteProviderCredential, useProviderCredentials } from "../../api/queries";
import type { ProviderCredentialSummary } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import { AddProviderCredentialModal } from "./AddProviderCredentialModal";
import { DeleteProviderCredentialDialog } from "./DeleteProviderCredentialDialog";
import "./provider-credentials.css";

const PROVIDER_LABEL: Record<ProviderCredentialSummary["kind"], string> = {
  hetzner: "Hetzner",
};

const WRITE_ONLY_NOTE =
  "Provider tokens are write-only — sealed on write and never shown again. Rotate or add a new credential to replace one.";

export function ProviderCredentialsPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch } = useProviderCredentials(currentId, {
    cursor: paginator.cursor,
  });
  const deleteMutation = useDeleteProviderCredential(currentId);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProviderCredentialSummary | null>(null);

  // Mirrors the server's authz matrix exactly: provider_credential:create/
  // update/delete are admin-only (server/internal/authz/model.go) — holding
  // one of these tokens is the ability to spend the tenant's whole cloud
  // budget. This is UX only (S-8) — the server enforces the real decision
  // on every request regardless of what this check says.
  const isAdmin = session?.role === "admin";

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id, label } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Removed "${label}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't remove "${label}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const addButton = (
    <Button variant="primary" onClick={() => setAddOpen(true)}>
      Add credential
    </Button>
  );

  const columns: DataTableColumn<ProviderCredentialSummary>[] = [
    { key: "label", header: "Label", cell: (c) => c.label, className: "data-table__primary" },
    { key: "kind", header: "Provider", cell: (c) => PROVIDER_LABEL[c.kind] },
    { key: "createdBy", header: "Added by", cell: (c) => c.createdBy || "—", className: "data-table__secondary" },
    {
      key: "createdAt",
      header: "Added",
      cell: (c) => new Date(c.createdAt).toLocaleString(),
      className: "data-table__secondary",
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (c: ProviderCredentialSummary) => (
              <Button variant="danger" onClick={() => setDeleteTarget(c)}>
                Remove
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Provider credentials"
        description="Cloud provider tokens Comebye uses to provision compute on your behalf — the Hetzner half of the hero flow."
        actions={isAdmin ? addButton : undefined}
      />

      <p className="provider-credentials-note">{WRITE_ONLY_NOTE}</p>

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading provider credentials…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load provider credentials. Check your connection and try again."
        }
        renderError={(err) =>
          err instanceof ApiError && err.code === "not_implemented" ? (
            <EmptyState
              title="Cloud provisioning isn't enabled on this control plane"
              description="An administrator can turn it on server-side (COMEBYE_PROVISIONING=1). Nothing to configure here until then."
            />
          ) : undefined
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No provider credentials yet"
            description="Add a Hetzner API token to start provisioning instances."
            action={isAdmin ? addButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(c) => c.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="credentials"
            />
          </>
        )}
      </QueryStates>

      {isAdmin && <AddProviderCredentialModal open={addOpen} fleetId={currentId} onClose={() => setAddOpen(false)} />}

      {isAdmin && (
        <DeleteProviderCredentialDialog
          credential={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
